import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState } from '../index';
import { updateHallOfFame } from './playerStats';

export type PlayerBadges = {
    availableBadges?: any[];
    selectedBadges?: any[];
};

interface PlayerBadgesState {
    data: PlayerBadges | null;
    loading: boolean;
    error: string | null;
    lastFetched: string | null;
}

const initialState: PlayerBadgesState = {
    data: null,
    loading: false,
    error: null,
    lastFetched: null
};

const apiUrl = window.config.playerApiUrl;

export const fetchPlayerBadges = createAsyncThunk(
    'playerBadges/fetchPlayerBadges',
    async (_, { rejectWithValue, getState }) => {
        try {
            const state: RootState = getState() as RootState;

            if (state.authentication.access_token && state.authentication.auth_type === 'user') {
                const response = await axios.get<PlayerBadges>(`${apiUrl}/player/data/player-badges`, {
                    headers: {
                        Authorization: 'Bearer ' + state.authentication.access_token,
                    },
                });

                if (response.data) {
                    return { data: response.data, success: true };
                }
            }
            return rejectWithValue({
                error: 'Failed to fetch player badges',
            });
        } catch (err: any) {
            const errResp = { error: err.toString() };
            return rejectWithValue(errResp);
        }
    }
);

const playerBadgesSlice = createSlice({
    name: 'playerBadges',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchPlayerBadges.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateHallOfFame.fulfilled, (state, action: PayloadAction<{ selectedBadges: any[]; success: boolean; }>) => {
                if (!state.data) {
                    state.data = {};
                }

                state.data.selectedBadges = action.payload.selectedBadges;
            })
            .addCase(fetchPlayerBadges.fulfilled, (state, action: PayloadAction<{ data: PlayerBadges; success: boolean; }>) => {
                state.loading = false;
                state.data = action.payload.data;
                state.lastFetched = new Date().toISOString();
            })
            .addCase(fetchPlayerBadges.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || 'Unknown error occurred';
            });
    }
});

// Export selectors
export const selectPlayerBadges = (state: { playerBadges: PlayerBadgesState; }) => state.playerBadges.data;
export const selectAvailableBadges = (state: { playerBadges: PlayerBadgesState; }) => state.playerBadges.data?.availableBadges;
export const selectSelectedBadges = (state: { playerBadges: PlayerBadgesState; }) => state.playerBadges.data?.selectedBadges;
export const selectPlayerBadgesLoading = (state: { playerBadges: PlayerBadgesState; }) => state.playerBadges.loading;
export const selectPlayerBadgesError = (state: { playerBadges: PlayerBadgesState; }) => state.playerBadges.error;
export const selectPlayerBadgesLastFetched = (state: { playerBadges: PlayerBadgesState; }) => state.playerBadges.lastFetched;

export default playerBadgesSlice.reducer;
